'use client';

import React, {
  createContext,
  ReactNode,
  useContext,
  useMemo,
  useState,
} from 'react';
import { SanitySiteSettings } from '@repo/models/gatsby-graphql-types';
import { GLOBAL_PRODUCTS } from '@repo/utils/constants';
import { useInternationalContext } from '@repo/gatsby-localization-helpers';
import {
  getAlgoliaBlogIndexName,
  getAlgoliaPageIndexName,
  getAlgoliaProductIndexName,
  getAlgoliaProductsFilter,
  getIfbLocaleByCurrency,
} from '@repo/utils/algoliaCollectionConfig';
import { LocaleContext } from './LocaleContext';

export type SiteIdentifier =
  | 'petsafe'
  | 'petsafe-support'
  | 'ifb'
  | 'sportdog'
  | 'sportdog-support'
  | 'kurgo'
  | 'storybook';

export type SiteSettingsContextType = {
  siteSettings: SanitySiteSettings;
  siteIdentifier: SiteIdentifier;
  collectionGridContained: boolean;
  algoliaSearchSettings: {
    productIndexName: string;
    blogIndexName: string;
    pageIndexName: string;
    autocompleteOpen: boolean;
    getAlgoliaProductsFilter: () => string;
    getAlgoliaBlogsFilter: () => string;
    getAlgoliaPagesFilter: () => string;
    setAutocompleteOpen: (open: boolean) => void;
  };
};

const SiteSettingsContext = createContext<SiteSettingsContextType | undefined>(
  undefined
);

export type SiteSettingsProviderProps = {
  children: ReactNode;
  siteIdentifier: SiteIdentifier;
  useSiteSettings: (locale: string) => SanitySiteSettings;
};

function SiteSettingsProvider({
  children,
  useSiteSettings,
  siteIdentifier,
}: Readonly<SiteSettingsProviderProps>) {
  const { locale } = useInternationalContext({});
  const localeContext = useContext(LocaleContext);
  const currency = localeContext?.currency ?? 'USD';
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);

  const isAlgoliaSearchLocalized = ['sportdog', 'petsafe'].includes(
    siteIdentifier
  );

  const siteSettings = useSiteSettings(locale);

  const memoizedValue = useMemo(() => {
    const currencyAwareLocale = getIfbLocaleByCurrency(
      siteIdentifier,
      locale,
      currency || ''
    );
    const productIndexName = getAlgoliaProductIndexName(
      siteIdentifier,
      currencyAwareLocale
    );
    const blogIndexName = getAlgoliaBlogIndexName(
      siteIdentifier,
      currencyAwareLocale
    );
    const pageIndexName = getAlgoliaPageIndexName(
      siteIdentifier,
      currencyAwareLocale
    );

    const getAlgoliaLanguageFilter = () =>
      isAlgoliaSearchLocalized && locale ? `language:${locale}` : '';

    const getIfbLegacyPagesFilter = () => {
      if (siteIdentifier !== 'ifb') return getAlgoliaLanguageFilter();

      return currency === 'CAD'
        ? '(language:en-ca OR language:en-us)'
        : '(language:en OR language:en-us)';
    };

    const getLegacyAlgoliaProductsFilter = () => {
      if (siteIdentifier === 'ifb' && currency === 'CAD') {
        return 'NOT canBeShown:false AND cadPrice > 0';
      }

      const additionalLanguageFilter =
        isAlgoliaSearchLocalized && locale
          ? `AND ${getAlgoliaLanguageFilter()}`
          : '';

      return `NOT canBeShown:false ${additionalLanguageFilter}`;
    };

    const useNewAlgoliaConfig = ['petsafe', 'sportdog'].includes(
      siteIdentifier
    );
    const supportSiteIndexNames: Record<
      string,
      { product: string; blog: string }
    > = {
      'petsafe-support': {
        product: 'petsafe-support-pages',
        blog: 'petsafe-support-articles',
      },
      'sportdog-support': {
        product: 'sportdog-support-pages',
        blog: 'sportdog-support-articles',
      },
    };
    const supportSiteNames = supportSiteIndexNames[siteIdentifier];

    let resolvedProductIndexName = 'products';
    if (useNewAlgoliaConfig) {
      resolvedProductIndexName = productIndexName;
    } else if (supportSiteNames) {
      resolvedProductIndexName = supportSiteNames.product;
    } else if (isAlgoliaSearchLocalized) {
      resolvedProductIndexName = GLOBAL_PRODUCTS;
    }

    let resolvedBlogIndexName = 'blogs';
    if (useNewAlgoliaConfig) {
      resolvedBlogIndexName = blogIndexName;
    } else if (supportSiteNames) {
      resolvedBlogIndexName = supportSiteNames.blog;
    }

    let resolvedPageIndexName = 'pages';
    if (useNewAlgoliaConfig) {
      resolvedPageIndexName = pageIndexName;
    }

    return {
      siteSettings,
      siteIdentifier,
      collectionGridContained: siteIdentifier === 'petsafe',
      algoliaSearchSettings: {
        productIndexName: resolvedProductIndexName,
        blogIndexName: resolvedBlogIndexName,
        pageIndexName: resolvedPageIndexName,
        autocompleteOpen,
        getAlgoliaProductsFilter: useNewAlgoliaConfig
          ? getAlgoliaProductsFilter
          : getLegacyAlgoliaProductsFilter,
        getAlgoliaBlogsFilter: useNewAlgoliaConfig
          ? () => ''
          : getAlgoliaLanguageFilter,
        getAlgoliaPagesFilter: useNewAlgoliaConfig
          ? () => ''
          : getIfbLegacyPagesFilter,
        setAutocompleteOpen,
      },
    };
  }, [
    siteSettings,
    siteIdentifier,
    isAlgoliaSearchLocalized,
    locale,
    currency,
    autocompleteOpen,
  ]);

  return (
    <SiteSettingsContext.Provider value={memoizedValue}>
      {children}
    </SiteSettingsContext.Provider>
  );
}

export { SiteSettingsProvider, SiteSettingsContext };
